import { useEffect, useState } from 'react'
import { useLanguage } from '@/localization'
import { formatLocalizedDate, getInitialLanguage } from '../../shared/localization/core'

/**
 * Shared news fetch hooks for the hub. The platform news API
 * (`GET /api/news?tag=general`, `GET /api/news/:id`) is proxied through Vite to
 * the platform API on :8078. The hub only surfaces `general`-tagged posts;
 * game-specific posts live on their own sites.
 */

/** Tag categories supported by the news feed. */
export type NewsTag = 'general' | 'kf1' | 'kf2' | 'minecraft' | string

/** A single news item as returned by the platform news API. */
export interface NewsItem {
  id: string | number
  title: string
  excerpt?: string
  /** Full post body (shown on the detail page; line breaks preserved). */
  body?: string
  tag?: NewsTag
  /** Optional external "Source / read more" link. */
  link?: string | null
  pinned?: boolean
  published?: boolean
  /** ISO date string from the platform API. */
  created_at?: string
  updated_at?: string
  /** Fallback date key for alternate payload shapes. */
  date?: string
}

/** Resolve the canonical display date for a news item. */
export function itemDate(item: NewsItem): string {
  return item.created_at ?? item.date ?? ''
}

/** Format an ISO date string for display (e.g. "Jun 15, 2026"). */
export function formatDate(iso: string): string {
  return formatLocalizedDate(iso, getInitialLanguage())
}

/** Narrow an unknown API payload (bare array or `{ news: [...] }`) into items. */
function normalizeList(data: unknown): NewsItem[] {
  if (Array.isArray(data)) return data as NewsItem[]
  const wrapped = (data as { news?: NewsItem[] })?.news
  return Array.isArray(wrapped) ? wrapped : []
}

export type NewsListState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: NewsItem[] }

/**
 * Fetch the list of `general`-tagged news (pinned-first then newest, as ordered
 * by the API) once on mount.
 */
export function useNews(): NewsListState {
  const { apiPath } = useLanguage()
  const [state, setState] = useState<NewsListState>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ status: 'loading' })

    async function load() {
      try {
        const res = await fetch(apiPath('/api/news?tag=general'), { signal: controller.signal })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: unknown = await res.json()
        setState({ status: 'ready', items: normalizeList(data) })
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setState({ status: 'error' })
      }
    }

    load()
    return () => controller.abort()
  }, [apiPath])

  return state
}

export type NewsItemState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'notfound' }
  | { status: 'ready'; item: NewsItem }

/** Fetch a single news item by id. Returns a 404 state when missing/unpublished. */
export function useNewsItem(id: string | undefined): NewsItemState {
  const { apiPath } = useLanguage()
  const [state, setState] = useState<NewsItemState>({ status: 'loading' })

  useEffect(() => {
    if (!id) {
      setState({ status: 'notfound' })
      return
    }

    const controller = new AbortController()
    setState({ status: 'loading' })

    async function load() {
      try {
        const res = await fetch(apiPath(`/api/news/${encodeURIComponent(id as string)}`), {
          signal: controller.signal,
        })
        if (res.status === 404) {
          setState({ status: 'notfound' })
          return
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: unknown = await res.json()
        // Accept either a bare item or `{ news: {...} }` / `{ item: {...} }`.
        const item =
          (data as { news?: NewsItem }).news ??
          (data as { item?: NewsItem }).item ??
          (data as NewsItem)
        if (!item || typeof item !== 'object' || !('id' in item)) {
          setState({ status: 'notfound' })
          return
        }
        setState({ status: 'ready', item })
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        setState({ status: 'error' })
      }
    }

    load()
    return () => controller.abort()
  }, [apiPath, id])

  return state
}
